/**
 * HandwritingConverterModal — Drop in PDFs, pick a handwriting style, get
 * back human-looking handwritten letters.  Each upload is rendered into
 * one of 13 readable handwriting fonts and any "Sincerely, [Name]" sign-off
 * becomes a real cursive signature that's unique-per-client (deterministic
 * hash of the name, so re-running the same letter gives the same look).
 *
 * Plugged into FTCReportBuilderNew between the report-download step and
 * the Copy & Fax prompt.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Upload, Download, Loader2, PenLine, FileText, Trash2, Eye, EyeOff,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
// Match the backend cap so we reject locally before wasting an upload.
const MAX_FILE_BYTES = 25 * 1024 * 1024;

// Map our style ids to the CSS font-family that Google Fonts ships under.
// Used to render each button label IN its own handwriting style so users
// can see what each one looks like at a glance, without having to cycle.
const STYLE_FONT_FAMILY = {
  caveat:               "'Caveat', cursive",
  kalam:                "'Kalam', cursive",
  patrick_hand:         "'Patrick Hand', cursive",
  architects_daughter:  "'Architects Daughter', cursive",
  indie_flower:         "'Indie Flower', cursive",
  shadows_into_light:   "'Shadows Into Light', cursive",
  homemade_apple:       "'Homemade Apple', cursive",
  gloria_hallelujah:    "'Gloria Hallelujah', cursive",
  just_another_hand:    "'Just Another Hand', cursive",
  rock_salt:            "'Rock Salt', cursive",
  coming_soon:          "'Coming Soon', cursive",
  schoolbell:           "'Schoolbell', cursive",
  nothing_you_could_do: "'Nothing You Could Do', cursive",
  // 10 new handwriting styles (Feb 2026).
  reenie_beanie:        "'Reenie Beanie', cursive",
  waiting_sunrise:      "'Waiting for the Sunrise', cursive",
  cedarville_cursive:   "'Cedarville Cursive', cursive",
  give_you_glory:       "'Give You Glory', cursive",
  dawning_new_day:      "'Dawning of a New Day', cursive",
  sue_ellen_francisco:  "'Sue Ellen Francisco', cursive",
  permanent_marker:     "'Permanent Marker', cursive",
  damion:               "'Damion', cursive",
  handlee:              "'Handlee', cursive",
  zeyada:               "'Zeyada', cursive",
  delius:               "'Delius', cursive",
  neucha:               "'Neucha', cursive",
  nanum_pen:            "'Nanum Pen Script', cursive",
  beth_ellen:           "'Beth Ellen', cursive",
  marck_script:         "'Marck Script', cursive",
  caveat_brush:         "'Caveat Brush', cursive",
  playpen_sans:         "'Playpen Sans', cursive",
  pangolin:             "'Pangolin', cursive",
  itim:                 "'Itim', cursive",
  edu_beginner:         "'Edu SA Beginner', cursive",
  mynerve:              "'Mynerve', cursive",
  comic_neue:           "'Comic Neue', cursive",
  merienda:             "'Merienda', cursive",
  courgette:            "'Courgette', cursive",
  playwrite_us:         "'Playwrite US Modern', cursive",
  sriracha:             "'Sriracha', cursive",
};
// Per-style size tweaks — some fonts are wider/taller than others, so this
// keeps every tile visually consistent.
const STYLE_FONT_SIZE = {
  rock_salt:            14,
  homemade_apple:       18,
  shadows_into_light:   22,
  just_another_hand:    26,
};

export default function HandwritingConverterModal({ open, onClose }) {
  const [style, setStyle] = useState("playpen_sans");
  const [tool, setTool] = useState("pen"); // 'pen' (default, near-black ballpoint) | 'pencil' (graphite)
  const [paper, setPaper] = useState("white"); // 'white' | 'aged' | 'notebook' | 'legal'
  const [finish, setFinish] = useState("original"); // 'original' | 'copied'
  // Default list is the FULL 13 — even if /styles is slow or blocked by a
  // browser extension, the picker still shows everything that the backend
  // supports.  /styles is treated as a refresh, not the source of truth.
  const [styles, setStyles] = useState([
    // Nov 2026 — user requested MORE neat handwritten fonts.  Six new
    // legible scripts added to the picker (Delius, Neucha, Nanum Pen,
    // Beth Ellen, Marck Script, Caveat Brush) while sloppy ones stay
    // retired.  Old style keys alias to "caveat" on the backend.
    { id: "playpen_sans", label: "Clear print (best for scanning)" },
    { id: "pangolin", label: "Bold clear print" },
    { id: "itim", label: "Rounded clear print" },
    { id: "edu_beginner", label: "School print" },
    { id: "mynerve", label: "Pen print" },
    { id: "comic_neue", label: "Easy-read print" },
    { id: "merienda", label: "Clear cursive" },
    { id: "courgette", label: "Bold clear cursive" },
    { id: "playwrite_us", label: "Upright school cursive" },
    { id: "sriracha", label: "Slanted pen cursive" },
    { id: "caveat", label: "Neat cursive" },
    { id: "kalam", label: "Casual print" },
    { id: "patrick_hand", label: "Friendly print" },
    { id: "architects_daughter", label: "Architect's block" },
    { id: "shadows_into_light", label: "Thin marker" },
    { id: "gloria_hallelujah", label: "Bouncy print" },
    { id: "rock_salt", label: "Bold marker" },
    { id: "coming_soon", label: "Soft block" },
    { id: "schoolbell", label: "Classroom" },
    { id: "reenie_beanie", label: "Loose slant" },
    { id: "cedarville_cursive", label: "Simple cursive" },
    { id: "permanent_marker", label: "Sharpie marker" },
    { id: "handlee", label: "Everyday hand" },
    // Six new neat handwritten fonts:
    { id: "delius", label: "Neat rounded" },
    { id: "neucha", label: "Marker note" },
    { id: "nanum_pen", label: "Ballpoint pen" },
    { id: "beth_ellen", label: "Refined script" },
    { id: "marck_script", label: "Elegant script" },
    { id: "caveat_brush", label: "Bold brush pen" },
  ]);
  const [files, setFiles] = useState([]); // [{ file, status, downloadUrl, downloadName, error }]
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Inline style preview (PDF blob in an iframe).
  const [showStylePreview, setShowStylePreview] = useState(false);
  const [stylePreviewUrl, setStylePreviewUrl] = useState("");
  const [stylePreviewLoading, setStylePreviewLoading] = useState(false);

  // Per-file output preview.
  const [previewIdx, setPreviewIdx] = useState(-1);

  useEffect(() => {
    if (!open) return;
    // Refresh the canonical list from the backend.  If this 404s/timeouts/
    // is blocked by an extension, we keep the full 13 baked-in defaults
    // and warn so the user knows what to retry.
    axios.get(`${API}/api/handwriting/styles`, { timeout: 15000 })
      .then((r) => { if (r.data?.styles?.length) setStyles(r.data.styles); })
      .catch(() => { toast.warning("Couldn't refresh style list — using built-in styles."); });
  }, [open]);

  // Refetch the sample whenever the style changes if the preview pane is open.
  useEffect(() => {
    if (!open || !showStylePreview) return;
    let cancelled = false;
    setStylePreviewLoading(true);
    axios.get(`${API}/api/handwriting/sample`, {
      params: { style, tool, paper, finish },
      responseType: "blob",
    })
      .then((res) => {
        if (cancelled) return;
        const url = window.URL.createObjectURL(res.data);
        setStylePreviewUrl((prev) => {
          if (prev) { try { window.URL.revokeObjectURL(prev); } catch (_) { /* noop */ } }
          return url;
        });
      })
      .catch(() => { if (!cancelled) toast.error("Couldn't load style preview"); })
      .finally(() => { if (!cancelled) setStylePreviewLoading(false); });
    return () => { cancelled = true; };
  }, [open, showStylePreview, style, tool, paper, finish]);

  // Release blob URLs when the modal closes so we don't leak memory across
  // multiple open/close cycles.
  useEffect(() => {
    if (open) return;
    files.forEach((f) => { if (f.downloadUrl) { try { window.URL.revokeObjectURL(f.downloadUrl); } catch (_) { /* noop */ } } });
    if (stylePreviewUrl) { try { window.URL.revokeObjectURL(stylePreviewUrl); } catch (_) { /* noop */ } }
    setFiles([]);
    setStylePreviewUrl("");
    setShowStylePreview(false);
    setPreviewIdx(-1);
  }, [open]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const pdfs = incoming.filter(
      (f) => f.type === "application/pdf" || (f.name || "").toLowerCase().endsWith(".pdf")
    );
    if (pdfs.length === 0) {
      toast.error("Only PDF files are supported");
      return;
    }
    const accepted = [];
    const rejected = [];
    pdfs.forEach((f) => {
      if (f.size > MAX_FILE_BYTES) rejected.push(f);
      else accepted.push(f);
    });
    if (rejected.length) {
      toast.error(`${rejected.length} file(s) skipped — over the 25MB limit.`);
    }
    if (accepted.length === 0) return;
    setFiles((prev) => [...prev, ...accepted.map((f) => ({ file: f, status: "ready" }))]);
  };

  const removeFile = (idx) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Revoke the dropped item's blob if it had one.
      const dropped = prev[idx];
      if (dropped?.downloadUrl) {
        try { window.URL.revokeObjectURL(dropped.downloadUrl); } catch (_) { /* noop */ }
      }
      return next;
    });
    if (previewIdx === idx) setPreviewIdx(-1);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };

  const extractError = async (e) => {
    // axios with responseType=blob hides the JSON detail inside the Blob —
    // read it back as text so the user sees the real reason, not a useless
    // "HTTP 4XX" placeholder.
    const fallback = e?.message || "Conversion failed";
    const data = e?.response?.data;
    const status = e?.response?.status;
    if (e?.code === "ECONNABORTED") {
      return "Timed out — the file may be too long. Try splitting it.";
    }
    if (!data) {
      if (e?.message && /Network Error/i.test(e.message)) {
        return "Network error — check your connection or any ad-blocker / VPN.";
      }
      return fallback;
    }
    if (typeof data === "string") return data;
    if (data instanceof Blob) {
      try {
        const txt = await data.text();
        try {
          const j = JSON.parse(txt);
          return j.detail || j.message || txt.slice(0, 200);
        } catch (_) {
          return txt.slice(0, 200) || `HTTP ${status || "?"}`;
        }
      } catch (_) {
        return `HTTP ${status || "?"}`;
      }
    }
    return data.detail || data.message || fallback;
  };

  const convertAll = async () => {
    if (files.length === 0) {
      toast.error("Drop at least one PDF first");
      return;
    }
    setBusy(true);
    const updated = [...files];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === "done") continue;
      updated[i] = { ...updated[i], status: "converting", error: undefined };
      setFiles([...updated]);
      try {
        const form = new FormData();
        form.append("file", updated[i].file);
        form.append("style", style);
        form.append("tool", tool);
        form.append("paper", paper);
        form.append("finish", finish);
        const res = await axios.post(`${API}/api/handwriting/convert`, form, {
          responseType: "blob",
          timeout: 300000,
        });
        // Defensive: if the server returns an error in blob form, axios still
        // resolves (status 2xx).  Sniff the first bytes to make sure it's a PDF.
        const head = await res.data.slice(0, 5).text();
        if (!head.startsWith("%PDF-")) {
          const txt = await res.data.text();
          let detail = txt.slice(0, 200);
          try { const j = JSON.parse(txt); detail = j.detail || j.message || detail; } catch (_) { /* not json */ }
          throw new Error(detail);
        }
        const url = window.URL.createObjectURL(res.data);
        // Feb 2026: backend now returns X-Client-First / X-Client-Last
        // headers detected from the letter's signature/letterhead so we
        // can name the download after the client on the letter itself.
        // We fall back to the original filename if detection fails.
        const first = (res.headers?.["x-client-first"] || "").trim();
        const last = (res.headers?.["x-client-last"] || "").trim();
        let downloadName;
        if (first && last) {
          downloadName = `${first}_${last}_dispute_letter.pdf`;
        } else {
          const orig = updated[i].file.name.replace(/\.pdf$/i, "");
          downloadName = `${orig}_dispute_letter.pdf`;
        }
        updated[i] = {
          ...updated[i],
          status: "done",
          downloadUrl: url,
          downloadName,
        };
      } catch (e) {
        const msg = await extractError(e);
        updated[i] = { ...updated[i], status: "error", error: msg };
      }
      setFiles([...updated]);
    }
    setBusy(false);
    const okCount = updated.filter((u) => u.status === "done").length;
    const failed = updated.filter((u) => u.status === "error");
    if (okCount > 0) toast.success(`${okCount} letter${okCount === 1 ? "" : "s"} converted to handwriting`);
    if (failed.length) {
      const firstErr = failed[0].error || "Unknown error";
      toast.error(`${failed.length} file(s) failed: ${firstErr}`);
    }
  };

  const downloadOne = (item) => {
    const a = document.createElement("a");
    a.href = item.downloadUrl;
    a.download = item.downloadName;
    a.click();
  };

  const downloadAll = () => {
    files.filter((f) => f.status === "done").forEach((f, i) => {
      // Stagger so the browser doesn't drop simultaneous downloads.
      setTimeout(() => downloadOne(f), i * 250);
    });
  };

  const currentPreview = previewIdx >= 0 ? files[previewIdx] : null;

  const styleLabel = useMemo(() => {
    return styles.find((s) => s.id === style)?.label || style;
  }, [style, styles]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="handwriting-modal">
      <div className="bg-zinc-950 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[94vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-zinc-950 border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-500/15 border border-violet-500/40">
              <PenLine className="w-5 h-5 text-violet-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Handwritten Letter Converter</h2>
              <p className="text-[11px] text-zinc-500">Drop in dispute letters → get back handwritten versions with a real cursive signature</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800" data-testid="handwriting-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Writing tool: Pen vs Pencil — changes ink color, opacity range,
              and (for pencil) adds a subtle graphite grain to every glyph
              so the output reads as a real handwritten document and not a
              computer print. */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Writing tool</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTool("pen")}
                className={`px-4 py-3 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 text-sm font-semibold ${tool === "pen" ? "border-violet-500 bg-violet-500/15 text-violet-100" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600"}`}
                data-testid="handwriting-tool-pen"
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-black border border-zinc-600" />
                Pen (near-black ballpoint)
              </button>
              <button
                type="button"
                onClick={() => setTool("pencil")}
                className={`px-4 py-3 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 text-sm font-semibold ${tool === "pencil" ? "border-violet-500 bg-violet-500/15 text-violet-100" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600"}`}
                data-testid="handwriting-tool-pencil"
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-zinc-400 border border-zinc-500" />
                Pencil (graphite gray)
              </button>
            </div>
          </div>

          {/* Paper background — drawn under the handwriting so the letter
              looks like it was actually written on a specific kind of paper. */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Paper</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { id: "white",    label: "Clean white",      swatch: "#ffffff", swatchBorder: "#5a5a64" },
                { id: "aged",     label: "Aged off-white",   swatch: "#fbf6e8", swatchBorder: "#5a5a64" },
                { id: "notebook", label: "Notebook (ruled)", swatch: "linear-gradient(to bottom, #fff 0 6px, #a7c5ec 6px 7px, #fff 7px 14px, #a7c5ec 14px 15px, #fff 15px 22px, #a7c5ec 22px 23px)", swatchBorder: "#5a5a64" },
                { id: "legal",    label: "Legal pad",        swatch: "linear-gradient(to bottom, #fff6c2 0 6px, #738eC4 6px 7px, #fff6c2 7px 14px, #738eC4 14px 15px, #fff6c2 15px 22px, #738eC4 22px 23px)", swatchBorder: "#5a5a64" },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPaper(p.id)}
                  className={`p-2 rounded-lg border-2 transition-colors flex flex-col items-center gap-1.5 ${paper === p.id ? "border-violet-500 bg-violet-500/15" : "border-zinc-700 bg-zinc-900 hover:border-zinc-600"}`}
                  data-testid={`handwriting-paper-${p.id}`}
                >
                  <span
                    className="w-full h-9 rounded border"
                    style={{
                      background: p.swatch.startsWith("linear-gradient") ? p.swatch : p.swatch,
                      borderColor: p.swatchBorder,
                    }}
                  />
                  <span className={`text-[10px] font-semibold ${paper === p.id ? "text-violet-100" : "text-zinc-300"}`}>{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Finish: Original (just written) vs Copied (photocopied look —
              slight rotation, uniform toner ink, scattered toner dust). */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Finish</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFinish("original")}
                className={`px-4 py-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-0.5 text-sm font-semibold ${finish === "original" ? "border-violet-500 bg-violet-500/15 text-violet-100" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600"}`}
                data-testid="handwriting-finish-original"
              >
                <span>Original</span>
                <span className="text-[10px] font-normal text-zinc-500">Clean, just-written</span>
              </button>
              <button
                type="button"
                onClick={() => setFinish("copied")}
                className={`px-4 py-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-0.5 text-sm font-semibold ${finish === "copied" ? "border-violet-500 bg-violet-500/15 text-violet-100" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600"}`}
                data-testid="handwriting-finish-copied"
              >
                <span>Photocopied</span>
                <span className="text-[10px] font-normal text-zinc-500">Slight rotation, toner specks</span>
              </button>
            </div>
          </div>

          {/* Style picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400">
                Handwriting style · <span className="text-zinc-300 normal-case tracking-normal">{styleLabel}</span>
              </label>
              <button
                type="button"
                onClick={() => setShowStylePreview((v) => !v)}
                className="text-xs font-semibold text-violet-300 hover:text-violet-200 flex items-center gap-1"
                data-testid="handwriting-style-preview-toggle"
              >
                {showStylePreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showStylePreview ? "Hide preview" : "Preview style"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {styles.map((s) => {
                const ff = STYLE_FONT_FAMILY[s.id] || "inherit";
                const fs = STYLE_FONT_SIZE[s.id] || 20;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStyle(s.id)}
                    className={`px-3 py-3 rounded-lg border-2 transition-colors flex flex-col items-center justify-center gap-1 min-h-[68px] ${style === s.id ? "border-violet-500 bg-violet-500/15 text-violet-100" : "border-zinc-700 bg-zinc-900 text-zinc-100 hover:border-zinc-600"}`}
                    data-testid={`handwriting-style-${s.id}`}
                  >
                    <span
                      style={{ fontFamily: ff, fontSize: fs, lineHeight: 1.1 }}
                      className="text-center leading-tight"
                    >
                      {s.label}
                    </span>
                    <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold">{(STYLE_FONT_FAMILY[s.id] || "").split("'")[1] || s.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Inline style preview pane */}
            {showStylePreview && (
              <div className="mt-3 rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden" data-testid="handwriting-style-preview-pane">
                <div className="px-3 py-2 flex items-center justify-between border-b border-zinc-800 text-[11px] text-zinc-400">
                  <span>Live sample · {styleLabel}</span>
                  {stylePreviewLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />}
                </div>
                {stylePreviewUrl ? (
                  <iframe
                    src={stylePreviewUrl}
                    title="Style sample"
                    className="w-full bg-white"
                    style={{ height: 360 }}
                    data-testid="handwriting-style-preview-iframe"
                  />
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-zinc-500 text-xs">
                    {stylePreviewLoading ? "Rendering sample…" : "Loading…"}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragOver ? "border-violet-400 bg-violet-500/10" : "border-zinc-700 bg-zinc-900/40"}`}
            data-testid="handwriting-drop"
          >
            <Upload className="w-8 h-8 text-violet-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-300 mb-2">Drag &amp; drop PDF letters here, or</p>
            <label className="inline-block">
              <span className="px-4 py-2 rounded-lg bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-zinc-200 text-sm font-medium cursor-pointer">
                Choose files
              </span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                multiple
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                className="hidden"
                data-testid="handwriting-file-input"
              />
            </label>
            <p className="text-[11px] text-zinc-500 mt-2">PDF only · multiple files OK · 25MB max each</p>
          </div>

          {/* File queue */}
          {files.length > 0 && (
            <div className="space-y-2" data-testid="handwriting-queue">
              {files.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900 border border-zinc-800">
                  <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  <span className="flex-1 text-sm text-white truncate" title={item.file.name}>{item.file.name}</span>
                  <span className="text-[10px] text-zinc-500 whitespace-nowrap">{(item.file.size / 1024).toFixed(0)} KB</span>
                  {item.status === "converting" && <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />}
                  {item.status === "done" && (
                    <>
                      <button
                        onClick={() => setPreviewIdx(previewIdx === idx ? -1 : idx)}
                        className={`px-2.5 py-1 rounded text-xs font-semibold flex items-center gap-1 ${previewIdx === idx ? "bg-violet-600 text-white" : "bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700"}`}
                        data-testid={`handwriting-preview-${idx}`}
                      >
                        <Eye className="w-3 h-3" /> {previewIdx === idx ? "Hide" : "Preview"}
                      </button>
                      <button
                        onClick={() => downloadOne(item)}
                        className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1"
                        data-testid={`handwriting-download-${idx}`}
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                    </>
                  )}
                  {item.status === "error" && (
                    <div className="flex flex-col items-end gap-0.5 max-w-[55%]">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-rose-300 bg-rose-500/15 border border-rose-500/40 px-2 py-0.5 rounded">Failed</span>
                      <span className="text-[10px] text-rose-300 text-right break-words" title={item.error} data-testid={`handwriting-error-${idx}`}>{item.error || "Unknown error"}</span>
                      <button
                        onClick={() => {
                          setFiles((prev) => prev.map((it, i) => i === idx ? { ...it, status: "ready", error: undefined } : it));
                        }}
                        className="text-[10px] font-semibold text-violet-300 hover:text-violet-200 underline"
                        data-testid={`handwriting-retry-${idx}`}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {item.status !== "converting" && (
                    <button
                      onClick={() => removeFile(idx)}
                      className="text-zinc-500 hover:text-rose-300 p-1"
                      title="Remove"
                      data-testid={`handwriting-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Output preview pane */}
          {currentPreview && currentPreview.downloadUrl && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden" data-testid="handwriting-output-preview">
              <div className="px-3 py-2 flex items-center justify-between border-b border-zinc-800 text-[11px] text-zinc-400">
                <span className="truncate">Preview · {currentPreview.downloadName}</span>
                <button
                  onClick={() => setPreviewIdx(-1)}
                  className="text-zinc-400 hover:text-white"
                  data-testid="handwriting-output-preview-close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <iframe
                src={currentPreview.downloadUrl}
                title="Converted preview"
                className="w-full bg-white"
                style={{ height: 480 }}
                data-testid="handwriting-output-preview-iframe"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-200 text-sm font-medium"
            >
              Close
            </button>
            {files.some((f) => f.status === "done") && (
              <button
                onClick={downloadAll}
                className="px-4 py-2 rounded-lg bg-zinc-800 border border-emerald-500/40 hover:bg-zinc-700 text-emerald-300 text-sm font-semibold flex items-center gap-2"
                data-testid="handwriting-download-all"
              >
                <Download className="w-4 h-4" /> Download all
              </button>
            )}
            <button
              onClick={convertAll}
              disabled={busy || files.length === 0}
              className="px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-2"
              data-testid="handwriting-convert-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
              Convert {files.length > 0 ? `(${files.length})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
